import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Building2, MapPin, Package, CreditCard, CheckCircle, ChevronRight, ChevronLeft, Loader2, Save, X } from 'lucide-react'

const STORAGE_KEY = 'parts_vendor_onboarding_data'

type Step = 'welcome' | 'business_info' | 'location' | 'specialization' | 'banking' | 'verification' | 'complete'

interface VendorOnboardingData {
  business_name: string
  business_registration: string
  tax_pin: string
  business_phone: string
  business_email: string
  business_address: string
  latitude: number | null
  longitude: number | null
  parts_specialization: string[]
  bank_name: string
  bank_account_number: string
  mpesa_number: string
  terms_accepted: boolean
}

const PARTS_SPECIALIZATIONS = [
  'Engine Parts', 'Transmission Parts', 'Brake System', 'Suspension', 
  'Electrical', 'Body Parts', 'Filters', 'Fluids', 'Battery', 'Tyres',
  'Accessories', 'General Auto Parts'
]

export default function PartsVendorOnboarding() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<Step>('welcome')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [data, setData] = useState<VendorOnboardingData>({
    business_name: '',
    business_registration: '',
    tax_pin: '',
    business_phone: profile?.phone || '',
    business_email: profile?.email || '',
    business_address: '',
    latitude: null,
    longitude: null,
    parts_specialization: [],
    bank_name: '',
    bank_account_number: '',
    mpesa_number: '',
    terms_accepted: false
  })

  // Pre-fill data from existing profile and load from localStorage
  useEffect(() => {
    if (profile) {
      // Load saved data from localStorage
      const savedData = localStorage.getItem(STORAGE_KEY)
      const parsedData = savedData ? JSON.parse(savedData) : {}
      
      setData(prev => ({
        ...prev,
        ...parsedData,
        business_name: parsedData.business_name || profile.business_name || '',
        business_phone: parsedData.business_phone || profile.business_phone || profile.phone || '',
        business_email: parsedData.business_email || profile.business_email || profile.email || '',
        latitude: parsedData.latitude || profile.latitude || null,
        longitude: parsedData.longitude || profile.longitude || null
      }))
    }
  }, [profile])

  // Phone validation functions
  const handlePhoneKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow only numbers and + sign
    const char = e.key
    if (!/^[0-9+]$/.test(char)) {
      e.preventDefault()
    }
  }

  const handlePhoneChange = (value: string, field: keyof VendorOnboardingData) => {
    // Remove any non-numeric characters except +
    const cleaned = value.replace(/[^0-9+]/g, '')
    setData(prev => ({ ...prev, [field]: cleaned }))
  }

  // Save data to localStorage on each step change
  useEffect(() => {
    if (currentStep !== 'welcome' && currentStep !== 'complete') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    }
  }, [data, currentStep])

  const getCurrentLocation = () => {
    setLoading(true)
    setError(null)
    
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser')
      setLoading(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setData(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }))
        setLoading(false)
      },
      (err) => {
        setError('Unable to retrieve your location. Please enable location services.')
        setLoading(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  const toggleSpecialization = (spec: string) => {
    setData(prev => ({
      ...prev,
      parts_specialization: prev.parts_specialization.includes(spec)
        ? prev.parts_specialization.filter(s => s !== spec)
        : [...prev.parts_specialization, spec]
    }))
  }

  const validateStep = (step: Step): boolean => {
    setError(null)
    
    switch (step) {
      case 'business_info':
        if (!data.business_name.trim()) {
          setError('Business name is required')
          return false
        }
        if (!data.business_phone.trim()) {
          setError('Business phone is required')
          return false
        }
        if (!data.business_email.trim()) {
          setError('Business email is required')
          return false
        }
        return true
      case 'location':
        if (!data.business_address.trim()) {
          setError('Business address is required')
          return false
        }
        if (!data.latitude || !data.longitude) {
          setError('Please capture your location')
          return false
        }
        return true
      case 'specialization':
        if (data.parts_specialization.length === 0) {
          setError('Please select at least one parts specialization')
          return false
        }
        return true
      case 'banking':
        if (!data.bank_name.trim()) {
          setError('Bank name is required')
          return false
        }
        if (!data.bank_account_number.trim()) {
          setError('Bank account number is required')
          return false
        }
        if (!data.mpesa_number.trim()) {
          setError('M-Pesa number is required')
          return false
        }
        return true
      default:
        return true
    }
  }

  const nextStep = () => {
    if (!validateStep(currentStep)) return
    
    const steps: Step[] = ['welcome', 'business_info', 'location', 'specialization', 'banking', 'complete']
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1])
    }
  }

  const prevStep = () => {
    const steps: Step[] = ['welcome', 'business_info', 'location', 'specialization', 'banking', 'complete']
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1])
    }
  }

  const submitOnboarding = async () => {
    if (!data.terms_accepted) {
      setError('You must accept the terms and conditions')
      return
    }
    if (!profile) return
    
    setSaving(true)
    setError(null)

    try {
      // Update profile with onboarding data
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          business_name: data.business_name,
          business_registration: data.business_registration,
          tax_pin: data.tax_pin,
          business_phone: data.business_phone,
          business_email: data.business_email,
          business_address: data.business_address,
          latitude: data.latitude,
          longitude: data.longitude,
          parts_specialization: data.parts_specialization,
          bank_name: data.bank_name,
          bank_account_number: data.bank_account_number,
          mpesa_number: data.mpesa_number,
          onboarding_completed: true,
          verification_status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id)

      if (updateError) throw updateError

      // Clear localStorage after successful submission
      localStorage.removeItem(STORAGE_KEY)

      setCurrentStep('complete')
    } catch (err) {
      console.error('Onboarding error:', err)
      setError('Failed to complete onboarding. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const steps: Step[] = ['welcome', 'business_info', 'location', 'specialization', 'banking', 'verification', 'complete']
  const currentStepIndex = steps.indexOf(currentStep)
  const progressPercent = currentStepIndex > 0 ? ((currentStepIndex - 1) / (steps.length - 2)) * 100 : 0

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', background: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Progress Bar */}
        {currentStep !== 'welcome' && currentStep !== 'complete' && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#6b7280' }}>
              <span>Step {currentStepIndex} of {steps.length - 1}</span>
              <span>{Math.round(progressPercent)}% Complete</span>
            </div>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div 
                style={{
                  height: '100%',
                  width: `${progressPercent}%`,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '12px', left: 0, right: 0, height: '2px', background: '#e5e7eb', zIndex: 0 }} />
          {steps.map((step, index) => (
            <div key={step} style={{ position: 'relative', zIndex: 1, textAlign: 'center', flex: 1 }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: index <= currentStepIndex ? '#667eea' : 'white',
                border: '2px solid #667eea',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 0.5rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: index <= currentStepIndex ? 'white' : '#667eea'
              }}>
                {index < currentStepIndex ? '✓' : index + 1}
              </div>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: index <= currentStepIndex ? '#667eea' : '#9ca3af', textTransform: 'uppercase' }}>
                {step.replace('_', ' ')}
              </div>
            </div>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 'welcome' && (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <Package size={40} color="white" />
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937', marginBottom: '1rem' }}>
              Welcome to Mkononi Parts Vendor Portal
            </h1>
            <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: 1.6 }}>
              Complete your business profile to start selling auto parts to customers across Nairobi. We will help you set up your business information, location, specializations, and payment details.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <Building2 size={24} style={{ color: '#667eea', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>Business Profile</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Your business details</div>
              </div>
              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <MapPin size={24} style={{ color: '#667eea', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>Location</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Your shop location</div>
              </div>
              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <Package size={24} style={{ color: '#667eea', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>Specializations</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Parts you sell</div>
              </div>
              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <CreditCard size={24} style={{ color: '#667eea', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>Payment Setup</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Receive payments</div>
              </div>
            </div>
            <button
              onClick={() => setCurrentStep('business_info')}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Get Started <ChevronRight size={20} />
            </button>
          </div>
        )}

        {currentStep === 'business_info' && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Business Information</h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Business Name *
                </label>
                <input
                  type="text"
                  value={data.business_name}
                  onChange={(e) => setData(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="e.g., Auto Parts Kenya Ltd"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Business Registration Number
                </label>
                <input
                  type="text"
                  value={data.business_registration}
                  onChange={(e) => setData(prev => ({ ...prev, business_registration: e.target.value }))}
                  placeholder="e.g., BN/2023/123456"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Tax PIN
                </label>
                <input
                  type="text"
                  value={data.tax_pin}
                  onChange={(e) => setData(prev => ({ ...prev, tax_pin: e.target.value }))}
                  placeholder="e.g., A001234567P"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Business Phone *
                </label>
                <input
                  type="tel"
                  value={data.business_phone}
                  onChange={(e) => handlePhoneChange(e.target.value, 'business_phone')}
                  onKeyPress={handlePhoneKeyPress}
                  placeholder="e.g., +2547XXXXXXXXX"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Business Email *
                </label>
                <input
                  type="email"
                  value={data.business_email}
                  onChange={(e) => setData(prev => ({ ...prev, business_email: e.target.value }))}
                  placeholder="e.g., parts@autoparts.co.ke"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 'location' && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Business Location</h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Business Address *
                </label>
                <input
                  type="text"
                  value={data.business_address}
                  onChange={(e) => setData(prev => ({ ...prev, business_address: e.target.value }))}
                  placeholder="e.g., Industrial Area, Nairobi"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ padding: '1.5rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                      Capture Location
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      {data.latitude && data.longitude 
                        ? `Captured: ${data.latitude.toFixed(6)}, ${data.longitude.toFixed(6)}`
                        : 'Click button to capture your current location'
                      }
                    </div>
                  </div>
                  <button
                    onClick={getCurrentLocation}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1rem',
                      background: loading ? '#9ca3af' : '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <MapPin size={16} />}
                    {loading ? 'Capturing...' : 'Capture Location'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'specialization' && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Parts Specialization</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Select the types of auto parts you sell:</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {PARTS_SPECIALIZATIONS.map((spec) => (
                <button
                  key={spec}
                  onClick={() => toggleSpecialization(spec)}
                  style={{
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: data.parts_specialization.includes(spec) ? '2px solid #667eea' : '1px solid #e5e7eb',
                    background: data.parts_specialization.includes(spec) ? '#f0f4ff' : 'white',
                    color: data.parts_specialization.includes(spec) ? '#667eea' : '#374151',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 'banking' && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Payment Information</h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Bank Name *
                </label>
                <input
                  type="text"
                  value={data.bank_name}
                  onChange={(e) => setData(prev => ({ ...prev, bank_name: e.target.value }))}
                  placeholder="e.g., Equity Bank"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Bank Account Number *
                </label>
                <input
                  type="text"
                  value={data.bank_account_number}
                  onChange={(e) => setData(prev => ({ ...prev, bank_account_number: e.target.value }))}
                  placeholder="e.g., 1234567890"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  M-Pesa Number *
                </label>
                <input
                  type="tel"
                  value={data.mpesa_number}
                  onChange={(e) => handlePhoneChange(e.target.value, 'mpesa_number')}
                  onKeyPress={handlePhoneKeyPress}
                  placeholder="e.g., 07XXXXXXXXX"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 'verification' && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Review & Submit</h2>
            
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>Business Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                <div><span style={{ color: '#6b7280' }}>Name:</span> {data.business_name}</div>
                <div><span style={{ color: '#6b7280' }}>Phone:</span> {data.business_phone}</div>
                <div><span style={{ color: '#6b7280' }}>Email:</span> {data.business_email}</div>
                <div><span style={{ color: '#6b7280' }}>Address:</span> {data.business_address}</div>
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>Specializations</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {data.parts_specialization.map(spec => (
                  <span key={spec} style={{ padding: '0.25rem 0.75rem', background: '#667eea', color: 'white', borderRadius: '12px', fontSize: '0.8rem' }}>
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>Payment Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                <div><span style={{ color: '#6b7280' }}>Bank:</span> {data.bank_name}</div>
                <div><span style={{ color: '#6b7280' }}>Account:</span> {data.bank_account_number}</div>
                <div><span style={{ color: '#6b7280' }}>M-Pesa:</span> {data.mpesa_number}</div>
              </div>
            </div>

            <div style={{ padding: '1rem', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d', marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={data.terms_accepted}
                  onChange={(e) => setData(prev => ({ ...prev, terms_accepted: e.target.checked }))}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.9rem', color: '#92400e' }}>
                  I accept the terms and conditions for becoming a Mkononi Parts Vendor
                </span>
              </label>
            </div>
          </div>
        )}

        {currentStep === 'verification' && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Review & Submit</h2>
            
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>Business Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                <div><span style={{ color: '#6b7280' }}>Name:</span> {data.business_name}</div>
                <div><span style={{ color: '#6b7280' }}>Phone:</span> {data.business_phone}</div>
                <div><span style={{ color: '#6b7280' }}>Email:</span> {data.business_email}</div>
                <div><span style={{ color: '#6b7280' }}>Address:</span> {data.business_address}</div>
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>Specializations</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {data.parts_specialization.map(spec => (
                  <span key={spec} style={{ padding: '0.25rem 0.75rem', background: '#667eea', color: 'white', borderRadius: '12px', fontSize: '0.8rem' }}>
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>Payment Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                <div><span style={{ color: '#6b7280' }}>Bank:</span> {data.bank_name}</div>
                <div><span style={{ color: '#6b7280' }}>Account:</span> {data.bank_account_number}</div>
                <div><span style={{ color: '#6b7280' }}>M-Pesa:</span> {data.mpesa_number}</div>
              </div>
            </div>

            <div style={{ padding: '1rem', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d', marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={data.terms_accepted}
                  onChange={(e) => setData(prev => ({ ...prev, terms_accepted: e.target.checked }))}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.9rem', color: '#92400e' }}>
                  I accept the terms and conditions for becoming a Mkononi Parts Vendor
                </span>
              </label>
            </div>
          </div>
        )}

        {currentStep === 'complete' && (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <CheckCircle size={40} color="white" />
            </div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', marginBottom: '1rem' }}>
              Onboarding Complete!
            </h2>
            <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: 1.6 }}>
              Your vendor profile has been submitted for review. Our team will verify your information and approve your account. You will be notified once your account is active.
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#dc2626', marginBottom: '1rem', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {/* Navigation Buttons */}
        {currentStep !== 'welcome' && currentStep !== 'complete' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
            <button
              onClick={prevStep}
              disabled={currentStep === 'welcome'}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ChevronLeft size={16} /> Back
            </button>
            
            {currentStep === 'verification' ? (
              <button
                onClick={submitOnboarding}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: saving ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                {saving ? 'Submitting...' : 'Complete Onboarding'}
              </button>
            ) : currentStep === 'banking' ? (
              <button
                onClick={() => setCurrentStep('verification')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={nextStep}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
